import type { ReactNode } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import type { UpsellPaymentBranding } from '../api/paymentApi';
import {
  DEFAULT_PAYMENT_THEME,
  PAYMENT_CARD_CLASS,
  themeCssVars,
  type PaymentTheme,
} from '../utils/paymentDisplay';
import { PaymentFooter } from './PaymentFooter';
import { PaymentHeader } from './PaymentHeader';

type StatusTone = 'success' | 'warning' | 'info' | 'error';

const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'tw-bg-emerald-50 tw-text-emerald-600 tw-ring-emerald-100',
  warning: 'tw-bg-amber-50 tw-text-amber-600 tw-ring-amber-100',
  info: 'tw-bg-sky-50 tw-text-sky-600 tw-ring-sky-100',
  error: 'tw-bg-red-50 tw-text-red-600 tw-ring-red-100',
};

interface PaymentStatusScreenProps {
  /** Identité du garant / de la société quand elle est connue ; Affinicar sinon. */
  theme?: PaymentTheme;
  footerBranding?: UpsellPaymentBranding | null;
  icon: LucideIcon;
  tone: StatusTone;
  title: string;
  description: ReactNode;
  children?: ReactNode;
}

/** Écran d'état (succès, lien expiré, déjà utilisé, erreur) avec en-tête et pied de page. */
export function PaymentStatusScreen({
  theme = DEFAULT_PAYMENT_THEME,
  footerBranding = null,
  icon: Icon,
  tone,
  title,
  description,
  children,
}: PaymentStatusScreenProps) {
  return (
    <div className="tw-flex tw-min-h-screen tw-flex-col tw-bg-slate-50" style={themeCssVars(theme)}>
      <PaymentHeader theme={theme} />
      <main className="tw-mx-auto tw-w-full tw-max-w-xl tw-flex-1 tw-px-4 tw-py-10 sm:tw-py-16">
        <div className={`${PAYMENT_CARD_CLASS} tw-p-6 tw-text-center sm:tw-p-10`}>
          <span
            className={`tw-mx-auto tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-ring-8 ${TONE_CLASSES[tone]}`}
          >
            <Icon className="tw-h-8 tw-w-8" aria-hidden="true" />
          </span>
          <h1 className="tw-mb-0 tw-mt-6 tw-text-2xl tw-font-bold tw-text-gray-900">{title}</h1>
          <div className="tw-mt-3 tw-text-base tw-leading-relaxed tw-text-gray-600">{description}</div>
          {children}
        </div>
      </main>
      <PaymentFooter branding={footerBranding} />
    </div>
  );
}

/** Attente neutre (chargement du lien, redirection vers la signature). */
export function PaymentLoadingScreen({ message }: { message: string }) {
  return (
    <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-slate-50 tw-p-4">
      <div className="tw-text-center" role="status" aria-live="polite">
        <Loader2 className="tw-mx-auto tw-mb-4 tw-h-10 tw-w-10 tw-animate-spin tw-text-slate-400" aria-hidden="true" />
        <p className="tw-mb-0 tw-text-sm tw-text-gray-600">{message}</p>
      </div>
    </div>
  );
}
